<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import type { CloseEvent } from "~/core/models/CloseEvent.interface";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { useFetchUsers } from "~/modules/user/composables/useFetchUsers";
import type { User } from "~/modules/user/models/User.interface";
import { getProfileLabel } from "~/modules/user/utils/profileLabel";
import { Filters, SectorResource } from "~/types";

const props = defineProps<{
  sector: Sector;
}>();

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const currentSelectedUser: Ref<string | undefined> = ref(undefined);
const {
  isLoading,
  results: users,
  error,
  fetchData,
  canManageItems,
  getDescription,
  filters,
  setFilter,
  search,
  onSearch,
} = useFetchUsers({
  organizationCode: currentOrganization.value?.code,
  sectorCode: props.sector.code,
});

const selectedItems: Ref<User[]> = ref([]);
const displayActions = computed(() => canManageItems(props.sector));

const showUserForm: Ref<boolean> = ref(false);

const searchQuery = computed({
  get() {
    return (filters.get(Filters.USER_FIRST_OR_LAST_NAME) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.USER_FIRST_OR_LAST_NAME, searchQuery);
  },
});

async function fetchSectorUsers(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  const params = new Map();
  await fetchData(page, pageSize, params);
}

async function refresh() {
  await fetchSectorUsers();
  selectedItems.value = [];
}

function onRowClick(user: User) {
  showUserForm.value = true;
  currentSelectedUser.value = user.login;
}

function onClose(event: CloseEvent) {
  if (event.edited) {
    refresh();
  }
}

onSearch(refresh);

defineExpose({
  refresh,
});
</script>

<template>
  <container-component
    :px="0"
    :py="0"
    :is-loading="isLoading && !users"
    :error="error"
  >
    <search-bar
      v-model:search-query="searchQuery"
      class="mb-4"
      :search-placeholder="t('filters.searchByLastNameOrFirstName')"
      @clear="search"
      @search="search"
    />

    <sectors-details-table
      v-model="selectedItems"
      primary-key="login"
      :is-loading="isLoading"
      :selection="displayActions"
      :min-width="displayActions ? '37rem' : '35rem'"
      :items="users"
      :display-actions="displayActions"
      :resource="SectorResource.USER"
      :get-description="getDescription"
      :sector="sector"
      selection-message="sectors.selectedUsers"
      @pagination:get-next-page="fetchSectorUsers"
      @row-click="onRowClick"
      @actions:detach="refresh"
    >
      <lxc-table-column
        prop="lastName"
        :label="t('table.header.lastname')"
        min-width="10rem"
      />
      <lxc-table-column
        prop="firstName"
        :label="t('table.header.firstname')"
        min-width="10rem"
      />
      <lxc-table-column
        :label="t('table.header.profile')"
        width="15rem"
        class="!py-0"
      >
        <template #default="scope">
          <badge-for-list
            v-if="scope.row.profile"
            :label="getProfileLabel(scope.row.profile)"
            type="secondary"
          />
        </template>
      </lxc-table-column>
    </sectors-details-table>
  </container-component>
  <user-form
    v-model:show="showUserForm"
    :organization-code="currentOrganization?.code"
    :organization-type="OrganizationType.OPERATOR"
    :user-login="currentSelectedUser"
    @close="onClose"
  ></user-form>
</template>
